import { Alert, Anchor, Badge, Button, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { IconFile, IconFileTypePdf, IconFileZip } from '@tabler/icons-react';

import { useRegistryOrderDetail } from '@/hooks/useRegistryOrderDetail';
import { formatFileSize } from '@/types/attachment';
import {
  newerRegistryDigestStatus,
  type RegistryDeliveredFile,
  type RegistryOrder,
  type RegistryOrderMessage,
} from '@/types/registry-documents';
import { formatDueDate } from '@/utils/businessDays';
import { RegistryDigestSection } from '../RegistryDigest/RegistryDigestSection';
import { RegistryOrderMessageList } from './RegistryOrderMessages';

const KIND_ICONS: Record<string, typeof IconFile> = {
  pdf: IconFileTypePdf,
  zip: IconFileZip,
};

/**
 * The files delivered against one order and, under them, the messages the
 * research desk sent about it and the replies sent to them, read when the
 * row is opened.
 *
 * The download links are presigned and short-lived, so they are never
 * kept from the list: the detail is read on opening, on every change the
 * list reports for the order, and again before the links can expire.
 */
export function RegistryOrderDocuments({
  order,
  onReply,
  onReportContentChanged,
}: Readonly<{
  order: RegistryOrder;
  /** Open the reply dialog for one of the desk's messages. Omitted where
   * replying is not offered. */
  onReply?: (message: RegistryOrderMessage) => void;
  /** The digest was added to the report's document, which is now a
   * version newer: a surface that shows the report re-reads it. */
  onReportContentChanged?: () => void;
}>) {
  // What the list says about the order: a delivery moves the count, a
  // reply sent or a desk message answered moves the unanswered count, and
  // either touches the row.
  const { detail, isLoading, error, refetch } = useRegistryOrderDetail(
    order.id,
    `${order.receivedDocumentCount}:${order.replyCount}:${order.updatedAt ?? ''}`
  );

  if (error && !detail) {
    return (
      <Alert color="red" title="Could not load the documents">
        <Group justify="space-between" gap="xs">
          <Text size="sm">{error.message}</Text>
          <Button size="compact-xs" variant="light" color="red" onClick={() => void refetch()}>
            Try again
          </Button>
        </Group>
      </Alert>
    );
  }
  if (isLoading || !detail) {
    return (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="xs" c="dimmed">
          Loading documents…
        </Text>
      </Group>
    );
  }
  return (
    <Stack gap={6}>
      {error && (
        // The list on screen is the last good read: its links may be past
        // their hour.
        <Group gap="xs">
          <Text size="xs" c="orange">
            The download links could not be refreshed and may have expired.
          </Text>
          <Button size="compact-xs" variant="subtle" onClick={() => void refetch()}>
            Try again
          </Button>
        </Group>
      )}
      <RegistryOrderDocumentList documents={detail.documents} />
      {/* The detail was read just now and the row half a minute ago, or
          the other way round after a list refresh: the later reading of
          the extraction's state is the one to start from. */}
      <RegistryDigestSection
        order={order}
        status={newerRegistryDigestStatus(detail.digest, order.digest)}
        documents={detail.documents}
        catalogue={detail.catalogueSnapshot}
        reportContent={detail.reportContent}
        onAddedToReport={() => {
          // The detail carries the report's new version and the "Added to
          // report" line reads from it.
          void refetch();
          onReportContentChanged?.();
        }}
      />
      <RegistryOrderMessageList messages={detail.messages} order={order} onReply={onReply} />
    </Stack>
  );
}

/** The list itself, as data in and markup out. */
export function RegistryOrderDocumentList({
  documents,
}: Readonly<{ documents: RegistryDeliveredFile[] }>) {
  if (documents.length === 0) {
    return (
      <Text size="xs" c="dimmed">
        Nothing has arrived for this order yet.
      </Text>
    );
  }
  return (
    <Stack gap={6} role="list" aria-label="Delivered documents">
      {documents.map((file) => (
        <DocumentRow key={file.sha256} file={file} />
      ))}
    </Stack>
  );
}

function DocumentRow({ file }: Readonly<{ file: RegistryDeliveredFile }>) {
  const Icon = KIND_ICONS[file.kind] ?? IconFile;
  const name = file.filename?.trim() || 'Unnamed file';
  const facts = [formatFileSize(file.sizeBytes), file.storedAt && formatDueDate(file.storedAt)]
    .filter(Boolean)
    .join(' · ');

  return (
    <Group gap={6} wrap="wrap" role="listitem" data-testid={`registry-document-${file.sha256}`}>
      {/* The icon and the name break as one item: a long name wraps
          beside its icon instead of leaving the icon on a line alone. */}
      <Group gap={6} wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
        <Icon size={16} aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
        {file.downloadUrl ? (
          // A new tab: the link is presigned and cross-origin, and one
          // that has expired answers with an error page that must not
          // replace the app.
          <Anchor
            href={file.downloadUrl}
            download={name}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
          >
            {name}
          </Anchor>
        ) : (
          <Tooltip label="No download link could be made for this file">
            <Text size="sm">{name}</Text>
          </Tooltip>
        )}
      </Group>
      {file.productCode ? (
        <Badge size="xs" variant="light" color="blue">
          {file.productCode}
        </Badge>
      ) : (
        <Tooltip
          label={
            file.reason
              ? `Not matched to an ordered document: ${file.reason.replace(/_/g, ' ')}`
              : 'Not matched to an ordered document'
          }
        >
          <Badge size="xs" variant="light" color="gray">
            Unmatched
          </Badge>
        </Tooltip>
      )}
      <Text size="xs" c="dimmed">
        {facts}
      </Text>
    </Group>
  );
}
